/**
 * Экраны с данными: S-07, S-11, S-20, S-22, S-09. Раздел 7.
 * Оформление — «Чернила и бумага» (C:/tmp/rebrand/01-direction.md), тексты — 04-voice.md.
 */
import { useEffect, useState } from 'react'
import {
  fetchSummary, fetchSku, fetchActions, fetchHistory, fetchReports, fetchReport,
  type Summary, type PayoutWeek, type FinesWeek, type WeekMetric, type Drr, type Delta, type Tone, type SkuList, type ActionList, type ActionItem, type ReportList, type ReportItem, type ReportFull,
} from '../api/client.js'
import { Icon, StatusTag } from '../components/icons/Icon.js'
import { BarList, Meter, Spark } from '../components/charts/Charts.js'
import {
  Value, NoShop, Loading, SummarySkeleton, Failed, Empty, Truncation, useData, useRole, useScreenHead, type State,
} from './common.js'
import {
  AGENT_ORDER, actionTitle, agentIcon, agentTitle, dayMonth, dayMonthOf, decisionWord, insightTone, REVERSIBILITY, todayLong,
} from './words.js'
import artActions from '../assets/illustrations/actions.webp'
import artLog from '../assets/illustrations/log.webp'
import artReports from '../assets/illustrations/reports.webp'
import { cloudGet, cloudSet } from '../telegram/call.js'
import { useTwoCols } from '../wide.js'

/** Отметка «Как это работает» уже видел или скрыл — ставит и экран S-01 (help.tsx). */
export const OB_DONE = 'ob_done_v1'

const byAgentOrder = (a: ReportItem, b: ReportItem): number =>
  AGENT_ORDER.indexOf(a.agent) - AGENT_ORDER.indexOf(b.agent)

/** Строка отчёта: иконка агента (Пепел — у агентов нет своих цветов) + название + шеврон. */
function ReportRow({ r, onOpen }: { r: ReportItem; onOpen?: ((id: string) => void) | undefined }): React.JSX.Element {
  return (
    <button type="button" className="row row-tap has-ic" data-testid={'report-' + r.id} onClick={() => onOpen?.(r.id)}>
      <Icon name={agentIcon(r.agent)} className="ic-muted" />
      <span className="row-main">
        <span className="row-title">{agentTitle(r.agent)}</span>
        {r.source === 'template' && <span className="row-meta">По шаблону, без ИИ</span>}
      </span>
      <Icon name="nav-next" className="ic-chev row-end" />
    </button>
  )
}

// ── S-07 Сводка ─────────────────────────────────────────────────────────────
/**
 * Первый экран утра (01, раздел 5): главное число — «Выплата WB за неделю», пункты «Главного
 * за утро» и одна кнопка; ниже — неделя по дням (спарклайны) и ДРР со шкалой к цели (09-charts).
 * Все строки, выводы и тон приходят с сервера (/api/summary); клиент только печатает, а числовые
 * value берёт лишь для геометрии графиков. Сотруднику выплаты и штрафов сервер не отдаёт ([11.3]).
 *
 * ⚠️ Ничего не прыгает (01, чек-лист): экран рисуется целиком, только когда пришли сводка,
 * список отчётов и сам отчёт «Главное». До того — скелетон той же формы. Раньше «Главное за утро»
 * приходило третьим запросом и вставлялось сверху, сдвигая уже нарисованный экран.
 */
/**
 * Первый вход: «Как это работает» (S-01) предлагается карточкой над сводкой, а не открывается сам —
 * так не ломается вход по ссылке из отчёта. Карточка исчезает, когда человек открыл справку или скрыл её.
 */
function FirstRun({ onHowto }: { onHowto: () => void }): React.JSX.Element | null {
  const [show, setShow] = useState(false)
  useEffect(() => {
    let alive = true
    void cloudGet(OB_DONE).then((v) => { if (alive) setShow(v === null) })
    return () => { alive = false }
  }, [])
  if (!show) return null
  const done = (): void => { setShow(false); void cloudSet(OB_DONE, new Date().toISOString().slice(0, 10)) }
  return (
    <section className="card card-pad" data-testid="first-run">
      <h2 className="frame-title">Впервые в Штабе?</h2>
      <p className="body-text muted">Как устроены пять агентов, откуда цифры и что приходит в Telegram — 9 коротких шагов, около 4 минут.</p>
      <div className="pager">
        <button type="button" className="btn" data-testid="first-run-open" onClick={() => { done(); onHowto() }}>Как это работает</button>
        <button type="button" className="btn btn-soft" data-testid="first-run-hide" onClick={done}>Скрыть</button>
      </div>
    </section>
  )
}

export function ScreenSummary(
  { onReports, onReport, onAgents, onMetric, onHowto, onCalc }:
  { onReports: () => void; onReport: (id: string) => void; onAgents: () => void; onMetric: (code: string) => void; onHowto: () => void; onCalc: (filled: boolean) => void },
): React.JSX.Element {
  const role = useRole()
  const two = useTwoCols()
  const { s, reload } = useData(fetchSummary)
  const reports = useData(fetchReports)
  const lead = useLeadReport(reports.s)
  useScreenHead(undefined, s.kind === 'ok' && s.data.date_display !== undefined ? s.data.date_display : todayLong())

  if (s.kind === 'fail') return <Failed message={s.message} onRetry={() => { reload(); reports.reload() }} />
  // Сотруднику «Главное» и выплату сервер не отдаёт ([11.3]) — у него в скелетоне этих блоков нет.
  if (s.kind === 'loading' || lead.pending) return <SummarySkeleton lead={role === 'owner'} />
  const d: Summary = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  const r = lead.report

  const payout = d.payout_week != null && <PayoutCard p={d.payout_week} fines={d.fines_week ?? null} calc={d.calc_week ?? null} onOpen={() => onMetric('payout')} onCalc={onCalc} />
  const morning = <>
    {r !== null && <LeadBrief r={r} onOpen={() => onReport(r.id)} max={d.payout_week != null ? 2 : 3} />}
    <MorningReports s={reports.s} skip={r === null ? null : r.id}
      onReports={onReports} onReport={onReport} onAgents={onAgents} />
  </>
  const rest = <>
      {d.week !== undefined && (
        <>
          <h2 className="sect wb-overline">
            <span>За 7 дней</span>
            <span className="sect-aside">{d.week.period_display}</span>
          </h2>
          <section className="card" data-testid="screen-body">
            <WeekMetricRow m={d.week.orders} testid="kpi-orders" reason onOpen={() => onMetric('orders')} />
            {/* Причина «сравнения нет» у суммы та же, что у заказов (одни и те же дни) — второй раз не пишем. */}
            {d.week.order_sum !== null && <WeekMetricRow m={d.week.order_sum} testid="kpi-order-sum" reason={false} onOpen={() => onMetric('order_sum')} />}
            {d.drr != null && <DrrRow a={d.drr} onOpen={() => onMetric('drr')} />}
          </section>
          {d.week.orders.series.note !== null && (
            <div className="note" data-testid="week-note">
              <Icon name="section-blind" size={20} />
              <span>{d.week.orders.series.note}</span>
            </div>
          )}
        </>
      )}

      {d.coverage != null && (
        <div className="note" data-testid="coverage">
          <Icon name="section-blind" size={20} />
          <span className="note-lines">
            <span>{d.coverage.display}</span>
            {/* ⚠️ Число пробела приходит ГОТОВЫМ: клиент не вычитает сам.
                Живая модель однажды посчитала 248−231=17 самостоятельно
                и получила V6 — тот же риск и здесь. */}
            {d.coverage.gap_display !== null && <span data-testid="coverage-gap">{d.coverage.gap_display}</span>}
          </span>
        </div>
      )}
  </>

  // Широкое окно (компьютер): слева деньги и неделя, справа утренние отчёты. Узкое — прежний порядок.
  return (
    <>
      <FirstRun onHowto={onHowto} />
      {two
        ? <div className="cols"><div className="col">{payout}{rest}</div><div className="col">{morning}</div></div>
        : <>{payout}{morning}{rest}</>}
    </>
  )
}

const TONE_WORD: Record<Exclude<Tone, 'neutral'>, string> = { good: 'Хорошо', attn: 'Внимание', risk: 'Риск', fine: 'Штраф' }

/**
 * Изменение с тоном сервера. Смысловой цвет — только вместе с иконкой своей формы и словом
 * (01-direction): слово тона читает диктор, глазами его заменяет форма иконки. Без тона — серым.
 */
function DeltaLine({ delta }: { delta: Delta }): React.JSX.Element {
  if (delta.tone === 'neutral') return <span className="delta">{delta.text}</span>
  return (
    <span className={'tag tag-' + delta.tone + ' delta-tag'}>
      <Icon name={`status-${delta.tone}`} size={16} />
      <span className="vh">{TONE_WORD[delta.tone]}: </span>
      {delta.text}
    </span>
  )
}

/** Главная карточка: выплата WB за последнюю закрытую неделю, полосы «эта / прошлая», штрафы. */
function PayoutCard({ p, fines, calc, onOpen, onCalc }: { p: PayoutWeek; fines: FinesWeek | null; calc: Summary['calc_week'] | null; onOpen: () => void; onCalc: (filled: boolean) => void }): React.JSX.Element {
  return (
    <>
      <h2 className="sect wb-overline">
        <span>{p.title}</span>
        {p.period_display !== null && <span className="sect-aside">{p.period_display}</span>}
      </h2>
      <section className="card hero" data-testid="payout">
        {p.current.display !== null
          ? <p className="hero-amount" data-testid="payout-amount">{p.current.display}</p>
          : <p className="hero-reason" data-testid="payout-reason">Выплаты пока нет: {p.current.reason}.</p>}
        {p.delta !== null && <DeltaLine delta={p.delta} />}
        {p.bars.length > 0 && <BarList rows={p.bars} label={p.summary} layout="inline" />}
        {fines !== null && fines.current.display !== null && (
          <div className="hero-fines" data-testid="fines">
            {fines.tone !== 'neutral' && <Icon name={`status-${fines.tone}`} size={20} className={'tone-' + fines.tone} />}
            <span className="hero-fines-main">
              <span className="hero-fines-title">{fines.title}</span>
              {fines.delta !== null && <span className="hero-fines-sub">{fines.delta.text}</span>}
            </span>
            <span className="hero-fines-val">{fines.current.display}</span>
          </div>
        )}
        {calc != null && (
          <button type="button" className="row row-tap hero-calc" data-testid="calc-week" onClick={() => onCalc(calc.profit.display !== null)}>
            <span className="row-main">
              <span className="row-title">Прибыль недели</span>
              <span className="row-meta">{calc.profit.display === null ? 'введите себестоимость в калькуляторе прибыли' : calc.note}</span>
              {calc.yesterday != null && <span className="row-meta" data-testid="calc-yesterday">вчера {calc.yesterday.prelim ? '≈ ' : ''}{calc.yesterday.display}{calc.yesterday.prelim ? ' — предварительно' : ''}</span>}
            </span>
            <span className="row-val"><span className="num">{calc.profit.display ?? 'посчитать'}</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
        <button type="button" className="link-inline" data-testid="payout-open" onClick={onOpen}>Разбор выплаты по неделям</button>
      </section>
    </>
  )
}

/** Строка недели: название, итог, изменение — слева; спарклайн по дням — справа. */
function WeekMetricRow({ m, testid, reason, onOpen }: { m: WeekMetric; testid: string; reason: boolean; onOpen: () => void }): React.JSX.Element {
  return (
    <button type="button" className="kpi-row kpi-tap" data-testid={testid} onClick={onOpen}>
      <span className="kpi-main">
        <span className="kpi-title">{m.title}</span>
        <span className="kpi-value"><Value v={m.total} bare /></span>
        {m.delta !== null
          ? <DeltaLine delta={m.delta} />
          : m.total.reason !== null
            ? <span className="kpi-sub">Итога нет: {m.total.reason}</span>
            : reason && m.delta_reason !== null && <span className="kpi-sub">{m.delta_reason}</span>}
      </span>
      <span className="kpi-chart"><Spark points={m.series.points} label={m.series.summary} /></span>
    </button>
  )
}

/** ДРР за те же 7 дней: значение, расход и заказы из рекламы, шкала к цели — только когда цель задана. */
function DrrRow({ a, onOpen }: { a: Drr; onOpen: () => void }): React.JSX.Element {
  const scale = a.scale_max !== null && a.value.value !== null
  return (
    <button type="button" className="kpi-row kpi-row-drr kpi-tap" data-testid="kpi-drr" onClick={onOpen}>
      <span className="kpi-main">
        <span className="kpi-title">{a.title}</span>
        <span className="kpi-value"><Value v={a.value} bare /></span>
        {a.value.value === null && a.value.reason !== null && <span className="kpi-sub">{a.value.reason}</span>}
        {a.sub !== null && <span className="kpi-sub">{a.sub}</span>}
        {a.target_reason !== null && <span className="kpi-sub">Цель ДРР не задана — сравнить не с чем</span>}
      </span>
      {scale && (
        <span className="kpi-meter">
          {a.tone !== 'neutral' && <StatusTag tone={a.tone} />}
          <Meter value={a.value.value} max={a.scale_max} target={a.target?.value ?? null}
            targetCaption={a.target_caption} tone={a.tone} label={a.summary} />
        </span>
      )}
    </button>
  )
}

/**
 * Последний отчёт «Главное» и признак «ещё грузится». Сотруднику его не отдаёт сервер ([11.3]),
 * у отчётов могла не случиться «Главного» — тогда блока нет, и ждать нечего.
 */
function useLeadReport(s: State<ReportList>): { pending: boolean; report: ReportFull | null } {
  const [got, setGot] = useState<{ id: string; report: ReportFull | null } | null>(null)
  const items = s.kind === 'ok' ? s.data.items ?? [] : []
  const latest = items[0]?.local_date
  const mgrId = items.find((r) => r.agent === 'manager' && r.local_date === latest)?.id ?? null
  useEffect(() => {
    if (mgrId === null) return
    let alive = true
    void fetchReport(mgrId).then((x) => { if (alive) setGot({ id: mgrId, report: x.ok ? x.data : null }) })
    return () => { alive = false }
  }, [mgrId])
  const pending = s.kind === 'loading' || (mgrId !== null && got?.id !== mgrId)
  return { pending, report: mgrId !== null && got?.id === mgrId ? got.report : null }
}

function LeadBrief({ r, onOpen, max }: { r: ReportFull; onOpen: () => void; max: number }): React.JSX.Element {
  const o = r.output ?? {}
  // Сначала то, что требует внимания; на первом экране — не больше трёх пунктов (01, раздел 5),
  // а под карточкой выплаты — двух: иначе кнопка уходит под таббар (замер 13.10: 836 px при границе 780).
  const items = [...(o.insights ?? [])]
    .sort((a, b) => rank(a.severity) - rank(b.severity))
    .slice(0, max)
  const todo = (o.actions ?? []).length
  return (
    <>
      <h2 className="sect wb-overline">
        <span>Главное за утро</span>
        <span className="sect-aside">{dayMonth(r.local_date)}</span>
      </h2>
      <section className="card brief" data-testid="lead">
        {o.headline !== undefined && o.headline !== '' && <p className="brief-lead">{o.headline}</p>}
        {items.map((i, k) => {
          const tone = insightTone(i.severity)
          return (
            <div className="brief-item" key={k}>
              {tone !== null && <StatusTag tone={tone} />}
              <p className="brief-text">{i.text}</p>
            </div>
          )
        })}
        <button type="button" className="btn btn-block" data-testid="lead-open" onClick={onOpen}>
          {todo > 0 ? `Что сделать: ${todo}` : 'Открыть «Главное»'}
        </button>
      </section>
    </>
  )
}

const rank = (sev: string | undefined): number => (sev === 'critical' ? 0 : sev === 'warning' ? 1 : 2)

/** Вход в утренние отчёты: отчёты последнего утра строками, ниже — «Все отчёты». */
function MorningReports(
  { s, skip, onReports, onReport, onAgents }:
  { s: State<ReportList>; skip: string | null; onReports: () => void; onReport: (id: string) => void; onAgents: () => void },
): React.JSX.Element {
  const role = useRole()
  const items = s.kind === 'ok' ? s.data.items ?? [] : []
  const latest = items[0]?.local_date
  const today = items.filter((r) => r.local_date === latest && r.id !== skip).sort(byAgentOrder)
  return (
    <>
      <h2 className="sect wb-overline">
        <span>{skip === null ? 'Утренние отчёты' : 'Остальные отчёты'}</span>
        {latest !== undefined && <span className="sect-aside">{dayMonth(latest)}</span>}
      </h2>
      <section className="card" data-testid="reports-link">
        {s.kind === 'ok' && items.length === 0 && (
          <div className="empty-card">
            <Empty icon="section-reports" title="Отчётов пока нет"
              text={role === 'owner' ? 'Включите нужных агентов — первый отчёт придёт утром.' : 'Они появятся, когда владелец включит агентов.'}>
              {role === 'owner' && <button type="button" className="btn btn-soft" onClick={onAgents}>Включить агентов</button>}
            </Empty>
          </div>
        )}
        {today.map((r) => <ReportRow key={r.id} r={r} onOpen={onReport} />)}
        {(s.kind === 'fail' || items.length > 0) && (
          <button type="button" className="row row-tap row-link has-ic" data-testid="reports-all" onClick={onReports}>
            <Icon name="section-reports" className="ic-action" />
            <span className="row-main"><span className="row-title">Все отчёты</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
      </section>
    </>
  )
}

// ── S-11 Товары ─────────────────────────────────────────────────────────────
export function ScreenSku({ onOpen }: { onOpen: (nm: number) => void }): React.JSX.Element {
  const { s, reload } = useData(() => fetchSku(50))
  if (s.kind === 'loading') return <Loading label="Загрузка товаров" rows={6} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d: SkuList = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  if (d.items.length === 0) {
    return <Empty icon="tab-products" title="Товаров пока нет" text="Они появятся после первой загрузки карточек из кабинета WB." />
  }
  return (
    <>
      {d.period_display !== undefined && (
        <h2 className="sect wb-overline">
          <span>Заказы за 7 дней</span>
          <span className="sect-aside">{d.period_display}</span>
        </h2>
      )}
      <section className="card ledger" data-testid="screen-body">
        <div className="ledger-head sku-head" aria-hidden="true"><span>Товар</span><span>Сумма · штук</span></div>
        <div>
        {d.items.map((i) => {
          const name = i.title ?? i.vendor_code ?? `Артикул WB ${i.nm_id}`
          const meta = [i.vendor_code !== null && i.vendor_code !== name ? i.vendor_code : null,
            i.stocks_wb_units?.display != null ? `на WB ${i.stocks_wb_units.display}` : null].filter((x) => x !== null)
          return (
            <button type="button" className="row row-tap" key={i.nm_id} data-testid={'sku-' + i.nm_id} onClick={() => onOpen(i.nm_id)}>
              <span className="row-main">
                <span className="row-title">{name}</span>
                {meta.length > 0 && <span className="row-meta">{meta.join(' · ')}</span>}
              </span>
              <span className="row-val">
                {/* Подписи значений — для диктора (шапка столбцов скрыта от него); при крупном тексте видны всем. */}
                {/* Без права «Деньги магазина» суммы нет — тогда главным числом идут штуки. */}
                {i.orders_sum_rub !== null && <Value v={i.orders_sum_rub} label="заказано на сумму" />}
                <Value v={i.orders_count} label="заказов" sub={i.orders_sum_rub !== null} />
              </span>
            </button>
          )
        })}
        </div>
      </section>
      <Truncation note={d.truncation} />
    </>
  )
}

// ── S-09 Утренние отчёты ────────────────────────────────────────────────────
export function ScreenReports(
  { onOpen, onAgents }: { onOpen?: (id: string) => void; onAgents?: () => void } = {},
): React.JSX.Element {
  const role = useRole()
  const { s, reload } = useData(fetchReports)
  if (s.kind === 'loading') return <Loading label="Загрузка отчётов" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d: ReportList = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  if (d.items.length === 0) {
    return (
      <Empty art={artReports} title="Отчётов пока нет"
        text={role === 'owner'
          ? 'Включите нужных агентов в разделе «Ещё → Агенты» — первый отчёт придёт утром.'
          : 'Они появятся, когда владелец включит агентов.'}>
        {role === 'owner' && onAgents !== undefined && (
          <button type="button" className="btn" data-testid="enable-agents" onClick={onAgents}>Включить агентов</button>
        )}
      </Empty>
    )
  }
  // Группы по утрам: сервер отдаёт отчёты от новых к старым.
  const groups: Array<{ date: string; items: ReportItem[] }> = []
  for (const r of d.items) {
    const g = groups.at(-1)
    if (g !== undefined && g.date === r.local_date) g.items.push(r)
    else groups.push({ date: r.local_date, items: [r] })
  }
  return (
    <div data-testid="screen-body">
      {groups.map((g) => (
        <section key={g.date}>
          <h2 className="sect wb-overline"><span>{dayMonth(g.date)}</span></h2>
          <div className="card">
            {g.items.sort(byAgentOrder).map((r) => <ReportRow key={r.id} r={r} onOpen={onOpen} />)}
          </div>
        </section>
      ))}
    </div>
  )
}
